import { HttpErrorResponse } from '@angular/common/http';
import { extractMessageFromHttpError, resolveApiErrorMessage } from '../../core/http/api-http-error';

export {
  extractMessageFromHttpError as extractNestHttpMessage,
  resolveApiErrorMessage,
} from '../../core/http/api-http-error';
/** Fields that correspond to uniqueness / conflict errors from tenant create/update APIs. */
export type TenantUniqueFieldKey =
  | 'tenantSubdomain'
  | 'email'
  | 'gstn'
  | 'businessPan'
  | 'contactPhone';

/**
 * Maps Nest `ConflictException` / 409 messages to inline field errors for create-client / edit-client.
 */
export function tenantConflictFieldsFromMessage(msg: string): {
  mapped: Partial<Record<TenantUniqueFieldKey, string>>;
  fallbackBanner: string | null;
} {
  const t = msg.trim();
  if (!t) {
    return { mapped: {}, fallbackBanner: null };
  }
  const lower = t.toLowerCase();

  if (lower.includes('subdomain')) {
    return { mapped: { tenantSubdomain: t }, fallbackBanner: null };
  }
  if (
    lower.includes('email already') ||
    lower.includes('email is already') ||
    lower.includes('email exists') ||
    (lower.includes('email') && lower.includes('registered'))
  ) {
    return { mapped: { email: t }, fallbackBanner: null };
  }
  if (lower.includes('gst') || lower.includes('gstin')) {
    return { mapped: { gstn: t }, fallbackBanner: null };
  }
  if (
    lower.includes('business pan') ||
    /\bbusiness\s*_?\s*pan\b/i.test(t) ||
    (lower.includes('pan') &&
      !lower.includes('card image') &&
      !lower.includes('profile picture') &&
      !lower.includes('validation failed'))
  ) {
    return { mapped: { businessPan: t }, fallbackBanner: null };
  }
  if (lower.includes('contact number')) {
    return { mapped: { contactPhone: t }, fallbackBanner: null };
  }

  return { mapped: {}, fallbackBanner: t };
}

export interface TenantFormApiErrorResolution {
  fieldPatches: Partial<Record<TenantUniqueFieldKey, string>>;
  submitError: string;
  toastMessage: string;
}

/** Shared HTTP fallback for manage-client create/edit forms. */
export function parseTenantClientHttpError(err: unknown, notFoundMessage?: string): string {
  if (err instanceof HttpErrorResponse) {
    const fromBody = extractMessageFromHttpError(err);
    if (fromBody) {
      return fromBody;
    }
    if (err.status === 401) {
      return 'Please sign in again (session expired).';
    }
    if (err.status === 404 && notFoundMessage) {
      return notFoundMessage;
    }
    if (err.status === 0) {
      return 'Cannot reach the API. Is the backend running on port 8001?';
    }
  }
  return 'Request failed. Please try again.';
}

/** Maps tenant create/update API errors to field errors, banner text, and toast copy. */
export function resolveTenantFormApiError(
  err: unknown,
  options?: { notFoundMessage?: string },
): TenantFormApiErrorResolution {
  const msg = resolveApiErrorMessage(err);
  if (msg) {
    const { mapped } = tenantConflictFieldsFromMessage(msg);
    if (Object.keys(mapped).length > 0) {
      return { fieldPatches: mapped, submitError: '', toastMessage: msg };
    }
    return { fieldPatches: {}, submitError: msg, toastMessage: msg };
  }

  const fallback = parseTenantClientHttpError(err, options?.notFoundMessage);
  return { fieldPatches: {}, submitError: fallback, toastMessage: fallback };
}

export interface TenantFormApiErrorTarget {
  fieldErrors: Partial<Record<TenantUniqueFieldKey, string>>;
  submitError: string;
}

/** Apply resolved tenant API errors to a create/edit client form in one call. */
export function applyTenantFormApiErrorToForm(
  err: unknown,
  target: TenantFormApiErrorTarget,
  uiAlert: { showError: (message: string) => void },
  options?: { notFoundMessage?: string },
): void {
  const { fieldPatches, submitError, toastMessage } = resolveTenantFormApiError(err, options);
  target.fieldErrors = { ...target.fieldErrors, ...fieldPatches };
  target.submitError = submitError;
  uiAlert.showError(toastMessage);
}
